import * as React from "react";
import {Component } from "react";
import {observer} from "mobx-react";
import {Item, Table, Popup as SemanticPopup} from "semantic-ui-react";
import {NavigationConfiguration} from "../../../config/navigation-configuration";
import {Icon} from "../../../core/components/icon/icon";
import {Grundstueck} from "../../../models/grundstueck";
import {Eingang, Gebaeude, Wohnung} from "../../../models/gebaeude";
import {autorun, runInAction} from "mobx";
import {ContentInfoBox} from "./content-info-box";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SVG from "react-inlinesvg";
import rightArrow from "../../../assets/images/layout/right-arrow.svg";
import {EingangTable} from "./tables/eingang-table";
import {Koordinaten} from "./koordinaten";
import {WohnungTable} from "./tables/wohnung-table";
import _ from "lodash";
import {MapViewerStore} from "../../../stores/map-viewer-store-mobx";
import {withStore} from "../../../core/stores/abstract-app-store";

export interface EingangWohnung {
    addresse: string;
    eingang: Eingang;
    wohnungen: Wohnung[];
}

export interface ObjectResultGebaeudeProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    store?: MapViewerStore; // this is injected by withStore
    gebaeude: Gebaeude | null;
    prepareKontakt: (grundstueck: Grundstueck | undefined) => void;
    handleErrorAndNotFound: () => void;
    handleHighlight: (gebaeude: Gebaeude, geometry: __esri.Geometry) => void;
    handleHighlightClick: (e: React.MouseEvent | undefined,
        item: Eingang | __esri.Point | null, layerNo?: number) => void;
    handleShowGrundstueck: (egrid: string, e: React.MouseEvent) => void;
}

@observer
class ObjectResultGebaeudeBase extends Component<ObjectResultGebaeudeProps, any> {
    watchHandles: any[] = [];
    prepareKontakt: (grundstueck: Grundstueck | undefined) => void;
    constructor(props: ObjectResultGebaeudeProps) {
        super(props);
        this.prepareKontakt = this.props.prepareKontakt;
    }

    componentDidMount(): void {
        const { gebaeude, handleHighlight, store } = this.props;

        autorun((reaction: any) => {
            if (!store || !store.mapStore.view) {
                return;
            }
            if (!gebaeude) {
                // this.errorAndNotFound("grundstueck");
                return;
            }
            reaction.dispose();
            this.prepareKontakt(store?.oStore.grundstuecke.get(gebaeude.egrid));
            let geometry;
            if (Array.isArray(gebaeude.geometry)){
                if (gebaeude.geometry.length > 1) {
                    geometry = geometryEngine.union(gebaeude.geometry);
                } else if (gebaeude.geometry.length === 1) {
                    geometry = gebaeude.geometry[0];
                }
            } else {
                geometry = gebaeude.geometry;
            }
            handleHighlight(gebaeude, geometry);
            const newRoute = "/gebaeude/" + gebaeude.egid.toString();
            const breadcrumbs = new NavigationConfiguration();
            breadcrumbs.items = [
                { title: "Gebäude" },
                { href: newRoute, title: gebaeude.nummer }
            ];
            let pageTitle = "";
            switch (gebaeude.attributes.GSTAT) {
            case "1001":
                pageTitle += "Projektiertes ";
                break;
            case "1003":
                pageTitle += "Im Bau befindliches ";
                break;
            case "1007":
                pageTitle += "Abgebrochenes ";
                break;
            }
            pageTitle += "Gebäude Nr. " + gebaeude.nummer
                + " in " + gebaeude.gemeinde
                + ((gebaeude.gbperName)? " (" + gebaeude.gbperName + ")": "");
            runInAction(()=> {
                store?.appStore.setPageTitle(pageTitle);
                store?.appStore.setTopContent(null);
                store?.appStore.setBottomContent(null);
                store?.appStore.setBreadcrumbs(breadcrumbs);
                store?.appStore.setRoutePath(newRoute);
                store?.appStore.setObjectId(gebaeude.egid.toString());
                store?.appStore.setTopicById("gebaeude");
            });
        });
    }

    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }
    handleHighlightClick(e: React.MouseEvent | undefined,
                         item: Eingang | __esri.Point | null,
                         layerNo?: number | undefined): void {
        this.props.handleHighlightClick(e, item, layerNo);
    }
    render(): React.ReactElement | null {
        const { gebaeude, store } = this.props;
        if (!store || !store.mapStore.view) {
            return null;
        }
        if (!gebaeude) {
            return null;
        }
        const { appConfig, mapViewerConfig} = store;
        let infoBlock: React.ReactElement | null = null;
        let geometry;
        if (Array.isArray(gebaeude.geometry)){
            if (gebaeude.geometry.length > 1) {
                geometry = geometryEngine.union(gebaeude.geometry);
            } else if (gebaeude.geometry.length === 1) {
                geometry = gebaeude.geometry[0];
            }
        } else {
            geometry = gebaeude.geometry;
        }
        const wohnungData: EingangWohnung[] = [];
        if (gebaeude.eingaenge) {
            const e = _.orderBy(gebaeude.eingaenge, ["strasse", "nummer", "plz"], ["asc", "asc", "asc"]);
            e.forEach((eingang: Eingang) => {
                const wData: Wohnung[] = [];
                gebaeude.wohnungen?.forEach((w) => {
                    if (w.edid === eingang.edid){
                        wData.push(w);
                    }
                });
                const ew: EingangWohnung = {
                    addresse: eingang.strasse + " " + eingang.nummer + ", " + eingang.plz + " " + eingang.ort,
                    eingang,
                    wohnungen: wData
                };
                wohnungData.push(ew);
            });
        }
        const wohnungBlock: React.ReactElement[] = [];
        const description = (<Item.Content><Item.Header as={"h3"}>Gebäudeinformation</Item.Header>
            <Item.Description>
                <Table basic="very" compact>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell singleLine>Amtliche Gebäudenummer</Table.Cell>
                            <Table.Cell>{gebaeude.nummer}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Eidg. Gebäudeidentifikator (EGID)</Table.Cell>
                            <Table.Cell>{gebaeude.egid}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Gemeinde (BFS-Nr.)</Table.Cell>
                            <Table.Cell>{gebaeude.gemeinde} ({gebaeude.gemeindeBfsNr})</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Grundbuch (GB-Nr.)</Table.Cell>
                            <Table.Cell>{gebaeude.gbperName} {(gebaeude.gbperCode)
                                ? (<span>({gebaeude.gbperCode})</span>): null}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Grundstück (EGRID)</Table.Cell>
                            <Table.Cell>{(gebaeude.egrid) ? (
                                <a href={appConfig.basename + "grundstueck/" + gebaeude.egrid}
                                    onClick={(event: React.MouseEvent) =>
                                        this.props.handleShowGrundstueck(gebaeude.egrid, event)}>
                                    {gebaeude.attributes.PRZNUMMER + " (" + gebaeude.egrid + ") "}
                                    <span className={"sprite sprite--right-arrow"}><SVG src={rightArrow} /></span></a>
                            ) : (
                                gebaeude.attributes.PRZNUMMER + ""
                                + ((gebaeude.gbperName) ? ", "+gebaeude.gbperName: "")
                            )}
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell verticalAlign={"top"}>Adresse (EGID/EDID)</Table.Cell>
                            <Table.Cell>
                                <EingangTable
                                    data={gebaeude.eingaenge}
                                    handleHighlightClick={(e: React.MouseEvent | undefined, eingang: Eingang | null) =>
                                        this.handleHighlightClick(e, eingang)}
                                    key={"eingang-" + gebaeude.egid}/>
                            </Table.Cell>
                        </Table.Row>
                        <Koordinaten
                            mapViewerConfig={mapViewerConfig}
                            coordinates={gebaeude.coordinates}
                            handleHighlightClick={(e: React.MouseEvent | undefined, item: __esri.Point | null,
                                                   layerNo: number) => this.handleHighlightClick(e, item, layerNo)}
                            highlightable={(geometry.type!=="point")}/>
                        {(gebaeude.bezeichnung) ? (
                            <Table.Row>
                                <Table.Cell>Name des Gebäudes</Table.Cell>
                                <Table.Cell>{gebaeude.bezeichnung}</Table.Cell>
                            </Table.Row> ) : null }
                        <Table.Row>
                            <Table.Cell colSpan={2}><h3>Klassifizierung</h3></Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Gebäudestatus</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GSTAT} {gebaeude.status}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Gebäudekategorie</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GKAT} {gebaeude.attributes.GKAT_TXT}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Gebäudeklasse</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GKLAS} {gebaeude.attributes.GKLAS_TXT}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell colSpan={2}><h3>Lebenszyklus</h3></Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Baujahr</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GBAUJ}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Bauperiode</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GBAUP_TXT}</Table.Cell>
                        </Table.Row>
                        {(gebaeude.attributes.GRENJ) ? (
                            <Table.Row>
                                <Table.Cell>
                                    <SemanticPopup
                                        content={"Jahr der (letzten) wertvermehrenden Renovation des Gebäudes"}
                                        trigger={<span>Renovationsjahr <Icon name={"information"} /></span>} />
                                </Table.Cell>
                                <Table.Cell>{gebaeude.attributes.GRENJ}</Table.Cell>
                            </Table.Row> ) : null }
                        {(gebaeude.attributes.GRENP_TXT) ? (
                            <Table.Row>
                                <Table.Cell>
                                    <SemanticPopup
                                        content={"Periode der (letzten) Wert vermehrenden Renovation des Gebäude"}
                                        trigger={<span>Renovationsperiode <Icon name={"information"} /></span>} />
                                </Table.Cell>
                                <Table.Cell>{gebaeude.attributes.GRENP_TXT}</Table.Cell>
                            </Table.Row> ) : null }
                        {(gebaeude.attributes.GABBJ) ? ( <Table.Row>
                            <Table.Cell>Abbruchjahr</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GABBJ}</Table.Cell>
                        </Table.Row> ) : null }
                        <Table.Row>
                            <Table.Cell colSpan={2}><h3>Gebäudestruktur</h3></Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Anzahl Geschosse</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GASTW}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Anzahl Wohnungen</Table.Cell>
                            <Table.Cell>{gebaeude.attributes.GANZWHG}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell colSpan={2}><h3>Gebäudedimensionen</h3></Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Fläche</Table.Cell>
                            <Table.Cell>
                                {(gebaeude.flaeche) ?
                                    (new Intl.NumberFormat("de-CH").format(Math.round(gebaeude.flaeche))
                                        + " m²") : "?"}
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table></Item.Description>
        </Item.Content>);
        wohnungData.forEach((ew: EingangWohnung, index: number) => {
            const title = "Wohnungen " + ew.addresse;
            wohnungBlock.push (
                <WohnungTable
                    data={ew.wohnungen}
                    eingang={ew.eingang}
                    key={index + "-" + ew.wohnungen.length}
                    handleHighlightClick={(e: React.MouseEvent | undefined, eingang: Eingang | null) =>
                        this.handleHighlightClick(e, eingang)}
                    title={title}/>
            );
        });
        const infoItems: React.ReactElement[] = [];
        let gwrUrl = null;
        const gwrAction = appConfig.predefinedActions.find((action) => action.id === "gwr");
        if (gwrAction && gwrAction.url) {
            const curentDate = new Date();
            const dateString =  curentDate.getDate().toString().padStart(2, "0") + "."
                + (curentDate.getMonth() + 1).toString().padStart(2, "0") + "."
                + curentDate.getFullYear().toString();
            gwrUrl = gwrAction.url + "&EGID=" + gebaeude.egid
                + "&RefDate=" + dateString + "_23_59_59";
        }
        infoItems.push(
            <ContentInfoBox
                mapViewerConfig={mapViewerConfig}
                key={"gwr"}
                type={"gwr"}
                url={gwrUrl}
            />
        );
        if (infoItems.length > 0) {
            infoBlock = (<Item.Group className={"geo-more-info"} divided>{infoItems}</Item.Group>);
        }

        return (<div>
            <Item.Group>
                <Item>{description}</Item>
            </Item.Group>
            {wohnungBlock}
            {infoBlock}
        </div>);
    }
}

export const ObjectResultGebaeude = withStore(ObjectResultGebaeudeBase);
